import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import type { WorkObjectTypeIcon } from "./work-object-type-icons";
import { trpc, useTRPC } from "../../../shared/api/trpc";

export type WorkObjectTypeDefinition = Awaited<ReturnType<typeof trpc.workObjectTypes.list.query>>[number];
export type WorkObjectTypeCategory = Awaited<ReturnType<typeof trpc.workObjectTypes.listCategories.query>>[number];
export type { WorkObjectTypeIcon };

export type CreateWorkObjectTypeInput = {
  categoryId: string | null;
  icon: WorkObjectTypeIcon;
  key: string;
  name: string;
};

export type UpdateWorkObjectTypeInput = CreateWorkObjectTypeInput & {
  id: string;
};

function sortTypes(types: WorkObjectTypeDefinition[]) {
  return [...types].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}

function sortCategories(categories: WorkObjectTypeCategory[]) {
  return [...categories].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}

export function useWorkObjectTypes() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const typesQuery = useQuery(trpc.workObjectTypes.list.queryOptions());
  const categoriesQuery = useQuery(trpc.workObjectTypes.listCategories.queryOptions());
  const createTypeMutation = useMutation(trpc.workObjectTypes.create.mutationOptions());
  const createCategoryMutation = useMutation(trpc.workObjectTypes.createCategory.mutationOptions());
  const updateCategoryMutation = useMutation(trpc.workObjectTypes.updateCategory.mutationOptions());
  const archiveCategoryMutation = useMutation(trpc.workObjectTypes.archiveCategory.mutationOptions());
  const updateTypeMutation = useMutation(trpc.workObjectTypes.update.mutationOptions());
  const archiveTypeMutation = useMutation(trpc.workObjectTypes.archive.mutationOptions());
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setError(null);
    try {
      await Promise.all([typesQuery.refetch(), categoriesQuery.refetch()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load types.");
    }
  }, [categoriesQuery.refetch, typesQuery.refetch]);

  async function createType(input: CreateWorkObjectTypeInput) {
    setError(null);
    try {
      const created = await createTypeMutation.mutateAsync(input);
      queryClient.setQueryData<WorkObjectTypeDefinition[]>(trpc.workObjectTypes.list.queryKey(), (current = []) => sortTypes([...current, created]));
      return created;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create type.");
      throw err;
    }
  }

  async function createCategory(name: string) {
    setError(null);
    try {
      const created = await createCategoryMutation.mutateAsync({ name });
      queryClient.setQueryData<WorkObjectTypeCategory[]>(trpc.workObjectTypes.listCategories.queryKey(), (current = []) => sortCategories([...current, created]));
      return created;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create type category.");
      throw err;
    }
  }

  async function updateCategory(input: { id: string; name: string }) {
    setError(null);
    try {
      const updated = await updateCategoryMutation.mutateAsync(input);
      queryClient.setQueryData<WorkObjectTypeCategory[]>(trpc.workObjectTypes.listCategories.queryKey(), (current = []) =>
        current.map((category) => (category.id === updated.id ? updated : category)),
      );
      queryClient.setQueryData<WorkObjectTypeDefinition[]>(trpc.workObjectTypes.list.queryKey(), (current = []) =>
        current.map((type) =>
          type.categoryId === updated.id ? { ...type, category: updated, categoryId: updated.id } : type,
        ),
      );
      return updated;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update type category.");
      throw err;
    }
  }

  async function archiveCategory(id: string) {
    setError(null);
    try {
      await archiveCategoryMutation.mutateAsync({ id });
      queryClient.setQueryData<WorkObjectTypeCategory[]>(trpc.workObjectTypes.listCategories.queryKey(), (current = []) => current.filter((category) => category.id !== id));
      queryClient.setQueryData<WorkObjectTypeDefinition[]>(trpc.workObjectTypes.list.queryKey(), (current = []) =>
        current.map((type) => (type.categoryId === id ? { ...type, category: null, categoryId: null } : type)),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not archive type category.");
      throw err;
    }
  }

  async function updateType(input: UpdateWorkObjectTypeInput) {
    setError(null);
    try {
      const updated = await updateTypeMutation.mutateAsync(input);
      queryClient.setQueryData<WorkObjectTypeDefinition[]>(trpc.workObjectTypes.list.queryKey(), (current = []) =>
        current.map((type) => (type.id === updated.id ? updated : type)),
      );
      return updated;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update type.");
      throw err;
    }
  }

  async function archiveType(id: string) {
    setError(null);
    try {
      await archiveTypeMutation.mutateAsync({ id });
      queryClient.setQueryData<WorkObjectTypeDefinition[]>(trpc.workObjectTypes.list.queryKey(), (current = []) => current.filter((type) => type.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not archive type.");
      throw err;
    }
  }

  return {
    archiveCategory,
    archiveType,
    categories: categoriesQuery.data ?? [],
    createCategory,
    createType,
    data: typesQuery.data ?? [],
    error: error ?? (typesQuery.error instanceof Error ? typesQuery.error.message : typesQuery.error ? String(typesQuery.error) : categoriesQuery.error instanceof Error ? categoriesQuery.error.message : categoriesQuery.error ? String(categoriesQuery.error) : null),
    isArchiving: archiveCategoryMutation.isPending || archiveTypeMutation.isPending,
    isCreating: createTypeMutation.isPending || createCategoryMutation.isPending,
    isLoading: typesQuery.isPending || categoriesQuery.isPending,
    isUpdating: updateCategoryMutation.isPending || updateTypeMutation.isPending,
    reload,
    updateCategory,
    updateType,
  };
}
